import _ from 'lodash';
import { mapGetters, mapActions, mapState } from 'vuex';
import { initDycolor, Timeline, CreativePreview, permissions, CreativeRefresh } from '~';
import CampaignCreatives from '@/components/rabbit/campaign-creatives/CampaignCreatives.vue';
import { STATUSES, CAMPAIGN_TABS, CREATIVE_STATUSES, COMMENTABLE_TYPES, MODES } from '@/util/constants';
import CampaignNotification from '@/components/rabbit/campaign-notification/CampaignNotification.vue';
import CampaignStats from '@/components/rabbit/campaign-stats/CampaignStats.vue';
import IterationsCalendar from '@/components/rabbit/iterations-calendar/IterationsCalendar.vue';
import FillStatus from '@/components/rabbit/fill-status/FillStatus.vue';

initDycolor('campaign-step', require(`${process.env.DYCOLOR_REQUIRE}./CampaignStep.scss`)); // eslint-disable-line

export default {
  components: {
    Timeline,
    CreativeRefresh,
    CampaignCreatives,
    CampaignNotification,
    'campaign-stats': CampaignStats,
    'creative-preview': CreativePreview,
    'iterations-calendar': IterationsCalendar,
    'fill-status': FillStatus,
  },
  data() {
    return {
      statuses: STATUSES,
      tabs: CAMPAIGN_TABS,
      loading: false,
      creativesErrorMsg: '',
      creativesLinkText: '',
      leadReportPath: null,
    };
  },
  methods: {
    ...mapActions('campaign', ['reloadCampaign', 'getLeadsReport']),
    isStatus(...statuses) {
      return statuses.includes(this.campaign.baseStatus.code);
    },
    acceptPost(postId) {
      this.$store.dispatch('campaign/acceptPost', postId);
    },
    downloadReport() {
      window.open(this.reportPath);
    },
    downloadLeadsReport() {
      this.getLeadsReport()
        .then(result => {
          if (result.path) {
            this.leadReportPath = result.path;
            return window.open(result.path);
          }
          this.$toast.error({ message: this.$t('pos.campaign.unable-to-download') });
          return false;
        })
        .catch(e => {
          this.$toast.error({ message: e.toString() });
        });
    },
    openCreative(id) {
      this.$router.push({
        name: 'creative',
        params: { id },
      });
    },
    isApprovedCreative(status) {
      return status === CREATIVE_STATUSES.APPROVED;
    },
    updateComment() {
      this.loading = true;
      return this.$refs.comment.updateComment()
        .then(() => {
          this.$toast.success({ message: this.$t('pos.comment.comment-added') });
          this.loading = false;
        })
        .then(() => this.reloadCampaign())
        .catch(e => {
          this.$toast.error({ message: e.toString() });
          this.loading = false;
        });
    },
    onFillFacebookPage() {
      this.$root.$emit('show-facebook-page-modal');
    },
  },
  computed: {
    ...mapState('auth', { currentUser: 'user' }),
    ...mapState('i18n', ['interfaceLanguage']),
    ...mapState('genericConfig', ['config']),
    ...mapGetters('campaign', { campaign: 'currentCampaign' }),
    readyForCAP() {
      return _.get(this.campaign, 'ready_for_cap.all', false);
    },
    messagesNotReady() {
      const messages = {};
      _.forIn(this.campaign.ready_for_cap, (value, index) => {
        if (index !== 'all') {
          if (!value) {
            messages[index] = (this.$t(`pos.campaign.not-ready-${index}`));
          }
        }
      });
      return messages;
    },
    suspenedPhone() {
      const phone = this.$t('pos.campaign.suspended.phone');
      return `tel:${phone}`;
    },
    hasUnfinishedIteration() {
      let iterations = this.campaign.iterations;
      if (this.campaign.status.code === STATUSES.FIN) {
        iterations = iterations.filter(iteration => iteration.finished_at === null);
        return iterations.length > 0;
      }
      return false;
    },
    hasIterations() {
      const iterations = _.get(this.campaign, 'iterations', null);
      return Array.isArray(iterations) && iterations.length > 0;
    },
    facebookPage() {
      return this.campaign.fbpage;
    },
    reportPath() {
      const report = _.get(this.campaign, 'campaign_fin_reports', [])
        .find(r => r.language === this.interfaceLanguage);
      return report ? report.path : _.get(this.campaign, 'campaign_fin_reports.0.path', null);
    },
    canViewTimeline() {
      return permissions.canGlobal(this.currentUser, 'Campaign', ['viewTimeline']);
    },
    canViewLinkToWIPCreative() {
      return permissions.canGlobal(this.currentUser, 'Creative', ['viewAll']);
    },
    permissions() {
      return permissions.getPermissions(this.campaign);
    },
    canSeeSuspended() {
      const activeSuspensionIssues = _.get(this, 'campaign.issues', [])
        .filter(i => !i.finished_at)
        .filter(i => i.name === 'suspended')
        .length;

      if (activeSuspensionIssues) {
        return permissions.canGlobal(this.currentUser, 'Campaign', ['viewAll', 'viewStarted']);
      }
      return true;
    },
    subscriptionsEnabled() {
      if (_.get(this.campaign, 'mode', null) === MODES.subscription) {
        return _.has(this.config, 'subscription_enabled');
      }
      return false;
    },
    comment() {
      return {
        commentable_id: this.campaign.id,
        commentable_type: COMMENTABLE_TYPES.CAMPAIGN,
      };
    },
    creativesInProgress() {
      let show = false;
      if (this.campaign.creatives && this.campaign.creatives.length > 0) {
        this.campaign.creatives.forEach(item => {
          if (item.status === CREATIVE_STATUSES.BRIEF) {
            this.creativesErrorMsg = this.$t('pos.campaign.creative-prepering');
            this.creativesLinkText = this.$t('pos.campaign.add-brief');
            show = true;
          } else if (!show && item.status === CREATIVE_STATUSES.READY) {
            this.creativesErrorMsg = this.$t('pos.campaign.creative-ready');
            this.creativesLinkText = this.$t('pos.campaign.review');
            show = true;
          }
        });
      }
      return show;
    },
    allCreatives() {
      const creatives = _.get(this.campaign, 'creatives', []);
      const response = {};
      Object.keys(CREATIVE_STATUSES).forEach(status => {
        response[status] = creatives.filter(c => c.status === status);
      });

      return response;
    },
    activeIssues() {
      return _.get(this, 'campaign.issues', [])
        .filter(i => !i.finished_at)
        .map(i => i.name);
    },
    disapprovalMessages() {
      return _.get(this, 'campaign.details', [])
        .filter(d => d.name === 'fb-status-message')
        .map(d => JSON.parse(d.value))[0] || null;
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/rabbit/campaign-step/CampaignStep.js